import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import intlShape from '@/shared/utils/intlShape';
import Toggle from 'react-toggle';

import UserSearch from '@/shared/components/input/UserSearch';
import { CapacityTaskSelect } from '@/shared/components/input/filterInputs/FilterCapacityTaskSelect';

import messages from '../hrmMessages';
import { FaUndo } from 'react-icons/fa';
import DelayedDateRangePicker from '@/shared/components/input/DelayedDateRangePicker';
import PeriodButtonGroup from '@/shared/components/PeriodButtonGroup';
import moment from 'moment';

const periods = [
  'week',
  'nextWeek',
  'month',
  'nextMonth',
  'quarter-1',
  'quarter',
  'nextQuarter',
  'year-1',
  'year',
  'year+1',
];

export const defaultFilters = {
  deadlineFrom: moment().startOf('year'),
  deadlineUntil: moment().endOf('year'),
  period: 'year',
  mainTaskTemplateId: undefined,
  showTaskCount: false,
};

class CapacityReportFilters extends Component {
  constructor(props) {
    super(props);
    this.state = { hasFilters: this.computeHasFilters(props.filters) };
  }

  computeHasFilters = filters => {
    const dateFromChanged =
      !!filters.deadlineFrom && !!defaultFilters.deadlineFrom
        ? !moment(filters.deadlineFrom).isSame(defaultFilters.deadlineFrom, 'day')
        : filters.deadlineFrom !== defaultFilters.deadlineFrom;

    const dateUntilChanged =
      !!filters.deadlineUntil && !!defaultFilters.deadlineUntil
        ? !moment(filters.deadlineUntil).isSame(defaultFilters.deadlineUntil, 'day')
        : filters.deadlineUntil !== defaultFilters.deadlineUntil;

    return (
      dateFromChanged ||
      dateUntilChanged ||
      filters.period !== defaultFilters.period ||
      filters.employeeId ||
      filters.employeeProfile ||
      filters.mainTaskTemplateId !== defaultFilters.mainTaskTemplateId ||
      filters.showTaskCount !== defaultFilters.showTaskCount
    );
  };

  filterChanged = filter => {
    const { filters, onFilterChange } = this.props;
    const newFilters = { ...filters, ...filter };
    const hasFilters = this.computeHasFilters(newFilters);

    if (JSON.stringify(filters) !== JSON.stringify(newFilters)) {
      onFilterChange(newFilters);
    }

    this.setState({ hasFilters });
  };

  resetFilters = () => {
    this.filterChanged({ ...defaultFilters });
  };

  dateRangeChanged = (startDate, endDate) => {
    this.filterChanged({ deadlineFrom: startDate, deadlineUntil: endDate });
  };

  render() {
    const { filters, columns, intl, availableMainTaskTemplateIds } = this.props;
    const { hasFilters } = this.state;

    return (
      <div className="info-block container-fluid">
        <div className="row">
          <div className="col-lg-3 col-md-6">
            <div className="form-group">
              <DelayedDateRangePicker
                startDate={filters.deadlineFrom ? moment(filters.deadlineFrom) : undefined}
                startDateId="CapacityReportFilterDeadlineFrom"
                endDate={filters.deadlineUntil ? moment(filters.deadlineUntil) : undefined}
                endDateId="CapacityReportFilterDeadlineUntil"
                onDatesChange={({ startDate, endDate }) =>
                  this.dateRangeChanged(startDate, endDate)
                }
                intl={intl}
                startDatePlaceholderText={intl.formatMessage(messages.DeadlineFrom)}
                endDatePlaceholderText={intl.formatMessage(messages.DeadlineUntil)}
                minimumNights={6}
                block
              />
            </div>
          </div>
          <div className="col-lg-9">
            <div className="form-group">
              <PeriodButtonGroup
                dateFrom={filters.deadlineFrom ? moment(filters.deadlineFrom) : undefined}
                dateUntil={filters.deadlineUntil ? moment(filters.deadlineUntil) : undefined}
                period={filters.period}
                onChange={({ dateFrom, dateUntil, period }) =>
                  this.filterChanged({ deadlineFrom: dateFrom, deadlineUntil: dateUntil, period })
                }
                visiblePeriods={periods}
              />
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-lg-3 col-md-6">
            <div className="form-group">
              <CapacityTaskSelect
                initialValue={filters.mainTaskTemplateId}
                onChange={value =>
                  this.filterChanged({
                    mainTaskTemplateId: value || undefined,
                  })
                }
                availableTemplateIds={availableMainTaskTemplateIds}
                placeholder={intl.formatMessage(messages.AllMainTasks)}
              />
            </div>
          </div>
          <div className="col-lg-3 col-md-6">
            <label className="react-toggle-wrapper margin5T">
              <Toggle
                checked={filters.showTaskCount || false}
                onChange={e => this.filterChanged({ showTaskCount: e.target.checked })}
              />
              <FormattedMessage
                className="label-text"
                id="ShowTaskCount"
                defaultMessage="Show task count"
              />
            </label>
          </div>
        </div>
        {hasFilters && (
          <div className="row">
            <div className="col-lg-4 col-md-6">
              <button
                type="button"
                className="btn btn-default btn-reset"
                onClick={() => this.resetFilters()}
              >
                <FaUndo className="react-icon" />
                &nbsp;
                <FormattedMessage {...messages.ResetFilters} />
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }
}

CapacityReportFilters.propTypes = {
  intl: intlShape.isRequired,
  filters: PropTypes.shape({
    deadlineFrom: PropTypes.string,
    deadlineUntil: PropTypes.string,
    period: PropTypes.string,
    mainTaskTemplateId: PropTypes.string,
    showTaskCount: PropTypes.bool,
  }),
  onFilterChange: PropTypes.func.isRequired,
  availableMainTaskTemplateIds: PropTypes.arrayOf(PropTypes.string),
};

CapacityReportFilters.defaultProps = {
  filters: { ...defaultFilters },
};
export default injectIntl(CapacityReportFilters);
